import { Modal } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { createFieldSelect, createFieldTextArea } from "../../../../components/input/template-builder/template-builder.logic";
import {
    createColumnBoolean,
    createColumnDate,
    createColumnInput,
    createColumnSelect
} from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import router from "../../../../router";
import store from "../../../../store";
import { dateFormat } from "../../../../utils/date";
import { conditionFormChecker, copyForm, putField } from "../../../../utils/form";
import { setProduct } from "./hc-info/hc-info.section";
import { defineAsyncComponent } from "vue";
import { callUser } from "../../storage-location/request/requestor/requestor.section";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const callRequestType = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "REQUEST_TYPE", value: searchQuery })
        .then(items => items.filter(item => ["1", "2", "4"].includes(item.key)));
export const callHcAttachment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "HC_ATTACHMENT", value: searchQuery });
export const callRequestStatus = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "REQUEST_STATUS", value: searchQuery })
        .then(items => items.filter(item => item?.attribute?.actionHcText));
export const callLicenseHcTerms = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "LICENSE_HC_TERMS", value: searchQuery });
export const callHc = ({ searchQuery, userInfo, isOfficerCreate }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "expireDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") }
    ];
    if (!isOfficerCreate) {
        where.push(
            { openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 1
            { name: "refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id },

            { logic: "OR", openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 2
            { name: "refUserCompany.userAuthorizes.isActive", type: "Boolean", operator: "=", value: true },
            { name: "refUserCompany.userAuthorizes.isTemp", type: "Boolean", operator: "=", value: false },
            { name: "refUserCompany.userAuthorizes.refStatus.key", type: "String", operator: "=", value: "active" },
            { name: "refUserCompany.userAuthorizes.refAuthorizeType.key", type: "String", operator: "=", value: "2" },
            { name: "refUserCompany.userAuthorizes.refAuthorize.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id },

            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.isActive", type: "Boolean", operator: "=", value: true },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.isTemp", type: "Boolean", operator: "=", value: false },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refStatus.key", type: "String", operator: "=", value: "active" },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refAuthorizeType.key", type: "String", operator: "=", value: "1" },
            {
                name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refAuthorize.id",
                type: "Getpath",
                operator: "=",
                value: "refUserCompany.id"
            },
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 2
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" } // 1
        );
    }
    if (searchQuery) {
        where.push({ name: "approveCode", type: "String", operator: "like", value: searchQuery });
    }

    return store
        .dispatch("resource/v1/transaction/hc/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "approveCode", "refUserCompany.id", "refUserCompany.fullNameTh"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "approveCode", type: "DESC" }]
            }
        })
        .then(page => page.data);
};

export const callHcFull = ({ id }) => {
    return store
        .dispatch("resource/v1/transaction/hc/search", {
            criteria: {
                select: ["**", "refUserCompany.refPersonType.*", "refUser.refPersonType.*", "refInform.**"],
                page: 0,
                size: 1,
                where: [{ name: "id", type: "UUID", operator: "=", value: id }],
                children: [
                    {
                        name: "refUserCompany.addresses",
                        select: ["*"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                    },
                    {
                        name: "refUser.addresses",
                        select: ["*"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                    },
                    {
                        name: "addresses",
                        select: ["**"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                    },
                    {
                        name: "attachments",
                        select: ["*"],
                        page: 0,
                        size: 1000000,
                        sort: [{ name: "createDate", type: "ASC" }]
                    },
                    {
                        name: "products",
                        select: [
                            "**",
                            "refInformProduct.**",
                            "refInformProduct.refHazObject.refSpecies.*",
                            "refInformProduct.refCoordinateCustoms.refUnit.*",
                            "refInformProduct.refCoordinateCustoms.refDetailSubStanceType.*",
                            "refInformProduct.refHaz.refManufactureCountry.*"
                        ],
                        page: 0,
                        size: 1000000,
                        sort: [{ name: "createDate", type: "ASC" }],
                        children: [
                            {
                                name: "productQties",
                                select: ["**"],
                                page: 0,
                                size: 1000000
                            }
                        ]
                    },
                    {
                        name: "declarations",
                        select: ["*", 'refDeclaration'],
                        page: 0,
                        size: 1000000,
                        sort: [{ name: "createDate", type: "DESC" }],
                    },
                    {
                        name: "refInform.requests",
                        asName: "refInform.lastRequestApprove",
                        select: ["*"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                        sort: [{ name: "createDate", type: "DESC" }],
                        children: [
                            {
                                name: "statuses",
                                asName: "lastStatusApprove",
                                select: ["**"],
                                page: 0,
                                size: 1,
                                where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                                sort: [{ name: "createDate", type: "DESC" }]
                            }
                        ]
                    }
                ]
            }
        })
        .then(page => page.data[0]);
};

export const createColumns = () => {
    const columns = [
        createColumnInput({
            title: () => i18n.global.t("model.hc.refUserCompany"),
            dataIndex: "refHcTemp.refUserCompany.companyNameTh",
            dataIndexValue: ({ record }) =>
                record.refHcTemp?.refUserCompany?.refPersonType?.key === "1"
                    ? record.refHcTemp?.refUserCompany?.companyNameTh
                    : record.refHcTemp?.refUserCompany?.fullNameTh,
            customRender: ({ record, column }) => column.dataIndexValue({ record })
        }),
        createColumnInput({ title: () => i18n.global.t("model.request.draftCode"), dataIndex: "draftCode", align: "center" }),
        createColumnInput({ title: () => i18n.global.t("model.request.requestCode"), dataIndex: "requestCode", align: "center" }),
        createColumnInput({ title: () => i18n.global.t("model.hc.approveCode"), dataIndex: "refHc.approveCode", align: "center" }),
        createColumnDate({ title: () => i18n.global.t("model.hc.expireDate"), dataIndex: "refHc.expireDate", align: "center" }),
        createColumnInput({
            title: () => i18n.global.t("model.hc.refInform"),
            dataIndex: "refHcTemp.refInform.approveCode",
            dataIndexValueObject: ({ record }) => {
                return [
                    record.refHcTemp?.refInform?.approveCode,
                    dateFormat({ text: record.refHcTemp?.refInform?.createDate, format: "DD/MM/YYYY" })
                ];
            },
            dataIndexValue: ({ record, column }) => {
                return column
                    .dataIndexValueObject({ record })
                    .filter(d => d)
                    .join(" ");
            },
            customRender: ({ record, column }) => {
                return (
                    <>
                        {column.dataIndexValueObject({ record }).map(d => (
                            <div>{d}</div>
                        ))}
                    </>
                );
            },
            align: "center"
        }),
        createColumnInput({
            title: () => i18n.global.t("model.haz.tradeNameTh"),
            dataIndex: "refHcTemp.products.refHaz.tradeNameTh",
            dataIndexValueObject: ({ record }) => {
                return record.refHcTemp?.products.map(d => [d.refInformProduct?.refHaz?.approveCode, d.refInformProduct?.refHaz?.tradeNameTh]);
            },
            dataIndexValue: ({ record, column }) => {
                return column
                    .dataIndexValueObject({ record })
                    .map(d => d.filter(d => d).join(" "))
                    .filter(d => d)
                    .join(", ");
            },
            customRender: ({ record, column }) => {
                return (
                    <>
                        {column
                            .dataIndexValueObject({ record })
                            .map(d => d.filter(d => d).map(d => <div>{d}</div>))
                            .map(d => (
                                <div>{d}</div>
                            ))}
                    </>
                );
            },
            align: "center"
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.request.refRequestType"),
            dataIndex: "refRequestType.value",
            customRender: ({ record }) => <div>
                <span>{record.refRequestType?.value}</span>
                {record.isSignCa ? <CIcon class="ml-1" file="sign.svg"></CIcon> : null}
            </div>,
            inputProp: () => ({ list: callRequestType, optLabel: "value" }),
            search: { name: "refRequestType.id" },
            align: "center"
        }),
        // createColumnSelect({
        //     title: () => i18n.global.t("model.hc.refStatus"),
        //     dataIndex: "refStatus.value",
        //     inputProp: () => ({ list: callRequestStatus, optLabel: "value" }),
        //     search: { name: "refStatus.id" },
        //     align: "center"
        // }),
        createColumnSelect({
            title: () => i18n.global.t("model.hc.refStatus"),
            dataIndex: "refStatus.attribute.actionHcText",
            inputProp: () => ({ list: callRequestStatus, optLabel: "attribute.actionHcText" }),
            customRender: ({ record }) => <div>
                <span>{record.refStatus?.attribute?.actionHcText}</span>
                <div>
                    { record.refStatus?.key === "waitApprove" && record.refHcTemp?.isConfirm ? `(${i18n.global.t("views.request.hcInfo.waitConfirm")})` : null }
                    { record.refStatus?.key === "waitApprove" && !record.refHcTemp?.isConfirm ? `(${i18n.global.t("views.request.hcInfo.waitInspection")})` : null }
                </div>
            </div>,
            search: { name: "refStatus.id" },
            align: "center"
        }),
        createColumnBoolean({
            visible: () => false,
            title: () => i18n.global.t("views.request.hcInfo.waitConfirm"),
            search: {
                name: "isWaitConfirm",
                type: "Boolean",
            },
            align: "center",
        }),
        createColumnBoolean({
            visible: () => false,
            title: () => i18n.global.t("views.request.hcInfo.waitInspection"),
            search: {
                name: "isWaitInspection",
                type: "Boolean",
            },
            align: "center",
        }),
    ];
    return columns;
};

export const createField = ({ props, data, refs, key }) => {
    const userInfo = store.state.resource.auth.info?.user;
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refRequestType`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.refRequestType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refRequestType,
                            "onUpdate:value": value => {
                                const oldValue = model.refRequestType;
                                model.refRequestType = value;
                                if (
                                    (["2"].includes(oldValue?.key) || (!["1"].includes(oldValue?.key) && ["1"].includes(value?.key))) &&
                                    model.refHc
                                ) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", { text: i18n.global.t("model.request.refRequestType") }),
                                        autoFocusButton: "cancel",
                                        onOk: () => {
                                            if (["1"].includes(value?.key)) {
                                                const { createForm } = require("./request.logic");
                                                cloneHc({
                                                    model: {
                                                        ...createForm({ props, data, refs }),
                                                        id: model.id,
                                                        version: model.version,
                                                        isActive: model.isActive,
                                                        refStatus: model.refStatus,
                                                        draftCode: model.draftCode,
                                                        requestCode: model.requestCode
                                                    }
                                                });
                                            } else {
                                                cloneHc({ model, id: model.refHc?.id });
                                            }
                                        },
                                        onCancel: () => {
                                            model.refRequestType = oldValue;
                                        }
                                    });
                                }
                            },
                            disabled: viewOnly,
                            list: callRequestType,
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refHc`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.storageLocation.approveCode"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.refHc,
                            "onUpdate:value": value => {
                                const oldValue = model.refHc;
                                model.refHc = value;
                                if (oldValue) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", {
                                            text: i18n.global.t("model.hc.approveCode")
                                        }),
                                        autoFocusButton: "cancel",
                                        onOk: () => cloneHc({ model, id: value?.id }),
                                        onCancel: () => {
                                            model.refHc = oldValue;
                                        }
                                    });
                                } else {
                                    cloneHc({ model, id: value?.id });
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) =>
                                callHc({
                                    searchQuery,
                                    userInfo: store.state.resource.auth.info?.user,
                                    isOfficerCreate: model.isOfficerCreate && !!userInfo.refOfficerLv,
                                }),
                            renderLabel: ({ item }) => (
                                <div>
                                    {item.approveCode} - {item.refUserCompany?.fullNameTh}
                                </div>
                            ),
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastStatus.forRequestRemark.0.remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.descToOfficer"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastStatus.forRequestRemark?.[0]?.remark,
                            "onUpdate:value": value => (model.lastStatus.forRequestRemark[0].remark = value),
                            disabled: viewOnly || model.isOfficerCreate
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastStatus.forReceiveRemark.0.remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.descToUserCompany"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastStatus.forReceiveRemark?.[0]?.remark,
                            "onUpdate:value": value => (model.lastStatus.forReceiveRemark[0].remark = value),
                            disabled: viewOnly || model.isOfficerCreate
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const cloneHc = ({ model, id }) => {
    if (id) {
        callHcFull({ id }).then(hc => {
            const refHcTemp = copyForm({ form: { ...hc, isTemp: true } });
            if (id) {
                refHcTemp.addresses.forEach((d, index) => (d.refRealId = hc.addresses[index].id));
                refHcTemp.attachments.forEach((d, index) => (d.refRealId = hc.attachments[index].id));
                refHcTemp.products.forEach((d, index) => {
                    d.refReal = { id: hc.products[index].id };
                    d.productQties.forEach((d2, index2) => (d2.refReal = { ...hc.products[index].productQties[index2] }));
                });
                setProduct({ products: refHcTemp.products });
            }
            return callUser({ userCompanyId: refHcTemp.refUserCompany.id }).then(users => {
                refHcTemp.refUser = users.find(d => d.id === store.state.resource.auth.info?.user?.id) || refHcTemp.refUser;
                router.push({
                    name: router.currentRoute.value.name,
                    params: {
                        props: JSON.stringify({
                            form: {
                                ...model,
                                refDepartment: hc.refDepartment,
                                refHcTemp
                            }
                        })
                    },
                    query: { _timestamp: new Date().getTime() }
                });
            });
        });
    } else {
        router.push({
            name: router.currentRoute.value.name,
            params: { props: JSON.stringify({ forceDefault: true, form: model }) },
            query: { _timestamp: new Date().getTime() }
        });
    }
};
